import mixin from 'privates/utils/mixin';
import Element from './Element';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ValueMixin from './mixins/ValueMixin';
import * as Utils from 'ui/Utils';
import {validate} from 'privates/validations/validation';
import * as members from 'privates/privateMembers';
import {runValidationsAfterChange} from 'privates/validationsService/validationsService';
import reporter from 'privates/reporter';
import {generateError} from 'privates/validations/errors';

/**
 * @class Slider
 * @summary Sliders allow users to select a number from a given range.
 * @classdesc
 *  Sliders offer your users a way of inputing a number by sliding a "handle" one
 *  way or the other along a track. You can set the range of values for the
 *  slider and also its default value.
 *
 *  The slider's values can be configured in one of two ways:
 *  + Value - You define the size of the steps between values, and the slider
 *    calculates how many steps fit in the slider's range.
 *  + Count - You define the number of steps between the values you want your
 *    slider to have, and the slider calculates the size of each step.
 * @mixes $w.ValueMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.FocusMixin
 * @memberof $w
 * @tagname slider
 * @viewername wixui.Slider
 */
class Slider extends Element {
  /**
   * @member min
   * @label property
   * @syntax
   * get min(): number
   * set min(value: number): void
   * @summary Sets or gets the slider's minimum value.
   * @snippet [Slider-min_get.es6=Get a slider's minimum value]
   * @snippet [Slider-min_set.es6=Set a slider's minimum value]
   * @type {external:Number}
   * @memberof $w.Slider
   * @instance
   */
    get min () {
        return getField.call(this, 'min', 0);
    }

    /**
     * @param {external:Number} val The slider's new min value.
     * @ignore
     */
    set min (val) {
        setField.call(this, 'min', val);

        const maxValue = getField.call(this, 'max');

        if (val > maxValue) {
            reporter.reportSdkWarning(generateError.warning_at_most({
                propertyName: 'min',
                wrongValue: val,
                maxValue
            }));
        }
    }

    /**
     * @member max
     * @label property
     * @syntax
     * get max(): number
     * set max(value: number): void
     * @summary Sets or gets the slider's maximum value.
     * @snippet [Slider-max_get.es6=Get a slider's maximum value]
     * @snippet [Slider-max_set.es6=Set a slider's maximum value]
     * @type {external:Number}
     * @memberof $w.Slider
     * @instance
     */
    get max () {
        return getField.call(this, 'max', 10);
    }

    /**
     * @param {external:Number} val The slider's new max value.
     * @ignore
     */
    set max (value) {
        setField.call(this, 'max', value);

        const minValue = getField.call(this, 'min');

        if (value < minValue) {
            reporter.reportSdkWarning(generateError.warning_at_least({
                propertyName: 'max',
                wrongValue: value,
                minValue
            }));
        }
    }

    /**
     * @member stepType
     * @label property
     * @syntax
     * get stepType(): string
     * set stepType(value: string): void
     * @summary Sets or gets a slider's step type.
     * @description
     *  The `stepType` determines how the slider's steps work:
     *
     *  The step type can be:
     *  + `"value"` - Each step's size is the number of units defined by the
     *    [`step`](#step) property's value. For example, suppose a slider has a
     *    [`min`](#min) value of `0`, [`max`](#max) value of `10`, and
     *    [`step`](#step) value of `5`. Each step's size will be 5 units, meaning
     *    there will be 2 steps.
     *  + `"count"` - The number of steps is determined by number the units
     *    in the [`step`](#step) property's value. For example, suppose a slider
     *    has a [`min`](#min) value of `0`, [`max`](#max) value of `10`, and
     *    [`step`](#step) value of `5`. There will be 5 steps, meaning each step
     *    will be 2 units.
     * @snippet [Slider-stepType_get.es6=Get a slider's step type]
     * @snippet [Slider-stepType_set.es6=Set a slider's step type]
     * @default `"value"`
     * @type {external:String}
     * @memberof $w.Slider
     * @instance
     */
    get stepType () {
        return getField.call(this, 'stepType', 'value');
    }

    /**
     * @param {external:String} val The slider's new stepType value.
     * @ignore
     */
    set stepType (value) {
        setField.call(this, 'stepType', value);
    }

    /**
     * @member step
     * @label property
     * @syntax
     * get step(): number
     * set step(value: number): void
     * @summary Sets or gets a slider's step value.
     * @description
     *  The `step` property along with the [`stepType`](#stepType) determes the
     *  size of each slider step and how many step units the slider is divided into.
     * @snippet [Slider-step_get.es6=Get a slider's step value]
     * @snippet [Slider-step_set.es6=Set a slider's step value]
     * @default `0.1`
     * @type {external:Number}
     * @memberof $w.Slider
     * @instance
     */
    get step () {
        return getField.call(this, 'step', 0.1);
    }

    /**
     * @param {external:String} val The slider's new step value.
     * @ignore
     */
    set step (val) {
        setField.call(this, 'step', val);
    }

    toJSON() {
        const {id, type, global, rendered, value, disabled, vertical, min, max, stepType, step} = this;
        return {id, type, global, rendered, value, disabled, vertical, min, max, stepType, step};
    }
}

function setField(key, value) {
    const RMI = members._RMI.get(this);
    const compId = members._compId.get(this);
    const compType = members._type.get(this).compType;

    const isValid = validate({
        compType,
        functionName: key,
        dataToValidate: {[key]: value},
        compName: this.id
    });
    if (!isValid) {
        return;
    }

    runValidationsAfterChange(RMI, compId, value);

    Utils.setData(this, {[key]: value});
}

function getField(key, defaultValue) {
    return Utils.getData(this, key) || defaultValue;
}

mixin(Slider.prototype, DisabledMixin.prototype);
mixin(Slider.prototype, HiddenCollapsedMixin.prototype);
mixin(Slider.prototype, FocusMixin.prototype);
mixin(Slider.prototype, ValueMixin.prototype);

export default Slider;



// WEBPACK FOOTER //
// ./js/modules/ui/Slider.es6